import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import pulseMain from '../Images/pulsetabletop.jpeg';
import pulseAlt from '../Images/pulsefinger.jpeg';
import PulseHeaderImage from '../Images/volumepage1.jpg';
import Footer from './Footer';

const imageDetails = {
  [pulseMain]: {
    category: 'Pulse Oximeters',
    heading: 'Table Top Pulse Oximeter',
    title: 'Model : MT 01 PLUS',
    description:
      'The MT 01 PLUS is a compact and reliable pulse oximeter designed for quick and accurate monitoring of blood oxygen saturation (SpO₂) and pulse rate. Featuring a clear display, user-friendly operation, and portable design, it is ideal for hospitals, clinics, emergency care, and home healthcare applications. With a wide measurement range and dependable performance, the MT 01 PLUS ensures efficient patient monitoring anytime, anywhere.',
    sections: {
      'Accurate Monitoring Performance': {
        icon: '📊',
        items: [
          'Precise SpO₂ measurement technology',
          'Real-time pulse rate monitoring',
          'Fast response and stable readings',
          'Suitable for adult, pediatric, and neonatal applications',
          'Reliable monitoring during continuous operation',
        ],
      },
      'Smart Display & Operation': {
        icon: '🖥️',
        items: [
          'Bright and clear display interface',
          'Easy-to-read monitoring parameters',
          'Simple and user-friendly operation',
          'Real-time waveform and data display',
          'Compact and portable design',
        ],
      },
      'Measurement Parameters': {
        icon: '📈',
        items: [
          'SpO₂ Range: 1% to 100%',
          'Pulse Rate: 20 to 250 bpm',
          'Pulse Amplitude: 0.03% to 20%',
          'Accurate signal acquisition and analysis',
          'Continuous monitoring support',
        ],
      },
      'Battery & Power Management': {
        icon: '🔋',
        items: [
          'Long battery backup for portable use',
          'Low power consumption',
          'Reliable performance during emergencies',
          'Battery status indication support',
          'Suitable for bedside and mobile applications',
        ],
      },
      'Electrical & Safety Features': {
        icon: '🔒',
        items: [
          'Stable electrical performance',
          'Safe and efficient operation',
          'Built-in protection mechanisms',
          'Designed for continuous clinical use',
          'Medical-grade monitoring reliability',
        ],
      },
      'Environmental Reliability': {
        icon: '🌡️',
        items: [
          'Suitable for hospital and clinical environments',
          'Stable performance across varying conditions',
          'Durable and lightweight construction',
          'Reliable operation during long monitoring sessions',
        ],
      },
      'Portable & Compact Design': {
        icon: '🩺',
        items: [
          'Lightweight and easy to carry',
          'Space-saving compact body',
          'Ideal for hospitals, ambulances, clinics, and home care',
          'Ergonomic and durable construction',
        ],
      },
    },
  },

  [pulseAlt]: {
    category: 'Pulse Oximeters',
    heading: 'Fingertip Pulse Oximeter',
    title: 'Model : MT 100',
    description:
      'MT 100 is an advanced and portable pulse oximeter designed for accurate SpO₂ and pulse rate monitoring with smart Bluetooth connectivity and companion app integration. With a bright OLED display, intelligent alarm system, and seamless wireless data synchronization, it is ideal for hospitals, clinics, home healthcare, and personal wellness monitoring.',
    sections: {
      'Accurate Health Monitoring': {
        icon: '💓',
        items: [
          'Reliable SpO₂ and pulse rate measurement',
          'Fast and stable reading performance',
          'Continuous monitoring support',
          'Suitable for daily and clinical use',
          'High-precision sensor technology',
        ],
      },
      'Smart Bluetooth Connectivity': {
        icon: '📱',
        items: [
          'Built-in Bluetooth data synchronization',
          'Companion app integration support',
          'Wireless monitoring and data transfer',
          'Easy report access through connected devices',
          'Real-time health data tracking',
        ],
      },
      'Smart OLED Display': {
        icon: '🖥️',
        items: [
          'High-clarity OLED display',
          'Adjustable brightness control',
          'Easy-to-read monitoring interface',
          'Clear parameter and waveform visibility',
          'Compact and modern display design',
        ],
      },
      'Intelligent Alarm System': {
        icon: '🔔',
        items: [
          'Alarm for abnormal readings',
          'Audible and visual alert notifications',
          'Quick response during irregular measurements',
          'Enhanced patient safety monitoring',
          'Reliable real-time warning support',
        ],
      },
      'Reliable Performance': {
        icon: '⚙️',
        items: [
          'Stable operation in various environments',
          'Durable and user-friendly design',
          'Smooth and efficient device functionality',
          'Designed for continuous healthcare monitoring',
        ],
      },
      'Portable & Power Efficient': {
        icon: '⚡',
        items: [
          'Lightweight and compact construction',
          'Low power consumption',
          'Portable design for easy carrying',
          'Suitable for hospital, travel, and home care use',
          'Long-lasting battery performance',
        ],
      },
    },
  },
};

const PulseoximeterDetail = () => {
  const allImages = [pulseMain, pulseAlt];

  const [mainImage, setMainImage] = useState(pulseMain);
  const [relatedImages, setRelatedImages] = useState(
    allImages.filter((img) => img !== pulseMain)
  );
  const [isMobile, setIsMobile] = useState(false);

  const introRef = useRef(null);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
    setRelatedImages(allImages.filter((img) => img !== clickedImage));

    setTimeout(() => {
      introRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  const detail = imageDetails[mainImage];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>Pulse Oximeter Machine | Mindron Meditech</title>
        <meta
          name="description"
          content="Explore pulse oximeter machines by Mindron Meditech including table top and fingertip pulse oximeters for accurate SpO₂ monitoring, pulse rate measurement, alarms, Bluetooth support, and healthcare applications."
        />
        <link
          rel="canonical"
          href="https://www.mindronmeditech.com/products/pulseoximeter"
        />
      </Helmet>

      <div
        style={{
          fontFamily: 'sans-serif',
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
        }}
      >
        {/* HERO */}
        <div
          style={{
            height: isMobile ? '30vh' : '80vh',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img
            src={PulseHeaderImage}
            alt="Hero"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              display: 'block',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
            }}
          />
        </div>

        {/* PRODUCT INTRO */}
        <div
          ref={introRef}
          style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '40px',
            padding: isMobile ? '32px 6%' : '48px 6%',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#e8f0fe',
                color: '#1a56db',
                fontSize: '13px',
                fontWeight: '600',
                padding: '4px 14px',
                borderRadius: '20px',
                marginBottom: '14px',
              }}
            >
              {detail?.category}
            </div>

            <h1
              style={{
                fontSize: isMobile ? '26px' : '38px',
                fontWeight: '800',
                color: '#0a1628',
                margin: '0 0 8px',
                lineHeight: 1.2,
              }}
            >
              {detail?.title}
            </h1>

            <h2
              style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '500',
                color: '#444',
                margin: '0 0 20px',
              }}
            >
              {detail?.heading}
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.8',
                maxWidth: '520px',
              }}
            >
              {detail?.description}
            </p>
          </div>

          <div
            style={{
              flex: '0 0 auto',
              width: isMobile ? '100%' : '340px',
              background: '#f4f6fb',
              borderRadius: '14px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={mainImage}
              alt="Pulse Oximeter"
              style={{
                width: '100%',
                maxHeight: '260px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </div>
        </div>

        {/* KEY FEATURES GRID */}
        <div
          style={{
            padding: isMobile ? '16px 6% 40px' : '16px 6% 56px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '28px',
            }}
          >
            Key Features
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div
                key={idx}
                style={{
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  padding: '22px 20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '14px',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>

                  <h3
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#0a1628',
                      margin: 0,
                    }}
                  >
                    {sectionName}
                  </h3>
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '18px',
                    listStyle: 'disc',
                  }}
                >
                  {sectionData.items.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: '1.8',
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* RELATED PRODUCTS */}
        {relatedImages.length > 0 && (
          <div
            style={{
              padding: isMobile ? '0 6% 48px' : '0 6% 60px',
            }}
          >
            <div
              style={{
                width: '50px',
                height: '5px',
                backgroundColor: '#cc0000',
                borderRadius: '2px',
                marginBottom: '12px',
              }}
            />

            <h2
              style={{
                fontSize: '22px',
                fontWeight: '800',
                color: '#0a1628',
                marginBottom: '24px',
              }}
            >
              Related Products
            </h2>

            <div
              style={{
                display: 'flex',
                gap: '20px',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: '4px',
              }}
            >
              {relatedImages.map((img, index) => (
                <div
                  key={index}
                  onClick={() => handleImageClick(img)}
                  style={{
                    flex: '0 0 auto',
                    width: '220px',
                    background: '#f4f6fb',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow =
                      '0 8px 20px rgba(0,0,0,0.13)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.07)';
                  }}
                >
                  <div
                    style={{
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '150px',
                    }}
                  >
                    <img
                      src={img}
                      alt={`Related ${index}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '120px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      background: '#001f4d',
                      padding: '10px 14px',
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: '13px',
                        color: '#fff',
                        fontWeight: '600',
                      }}
                    >
                      {imageDetails[img]?.title || `Product ${index + 1}`}
                    </p>

                    <p
                      style={{
                        margin: '4px 0 0',
                        fontSize: '11px',
                        color: '#aac4ff',
                      }}
                    >
                      {imageDetails[img]?.category}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Footer />

        <style>{`
          *::-webkit-scrollbar {
            display: none;
          }

          * {
            scrollbar-width: none;
          }

          body {
            overflow-x: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default PulseoximeterDetail;